import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { X, Wrench } from 'lucide-react';
import { logServiceRequest } from '../../api/seller.js';
import { Button } from './Button.js';

interface Props {
  registrationId: string;
  serialNumber:   string;
  onClose:        () => void;
}

export function ServiceModal({ registrationId, serialNumber, onClose }: Props) {
  const [problem, setProblem] = useState('');
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => logServiceRequest(registrationId, { problem }),
    onSuccess: () => {
      toast.success('Service request logged');
      qc.invalidateQueries({ queryKey: ['service', registrationId] });
      qc.invalidateQueries({ queryKey: ['seller-services'] });
      onClose();
    },
    onError: () => toast.error('Failed to log service request'),
  });

  const remaining = 1000 - problem.length;

  return (
    // Backdrop click-to-close is a mouse convenience only — the dialog's ✕
    // button (below) is the keyboard-accessible close path.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className="w-full max-w-md rounded-xl bg-white shadow-xl border border-slate-200 overflow-hidden"
        onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center">
              <Wrench className="w-3.5 h-3.5 text-blue-600" />
            </div>
            <h2 className="text-sm font-bold text-slate-900">Log Service Request</h2>
          </div>
          <button type="button" onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          <div className="bg-slate-50 border border-slate-100 rounded-lg px-3.5 py-2.5">
            <p className="text-xs text-slate-400 mb-0.5">Serial number</p>
            <p className="font-mono text-sm font-semibold text-slate-800">{serialNumber}</p>
          </div>

          <div>
            <label htmlFor="service-problem" className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">
              Describe the problem
            </label>
            <textarea id="service-problem" rows={5} value={problem} onChange={(e) => setProblem(e.target.value)}
              placeholder="What is the issue with this device?"
              maxLength={1000}
              className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all" />
            <p className={`text-xs mt-1 text-right ${remaining < 50 ? 'text-amber-600' : 'text-slate-400'}`}>
              {remaining} characters remaining
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2.5 border-t border-slate-100 px-5 py-3.5">
          <button type="button" onClick={onClose}
            className="px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
            Cancel
          </button>
          <Button type="button" onClick={() => mutation.mutate()}
            disabled={!problem.trim() || mutation.isPending}
            loading={mutation.isPending}>
            Submit request
          </Button>
        </div>
      </div>
    </div>
  );
}
